import { inject, Injectable } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ENVIRONMENTS } from '../enums/environments.enum';
import { SessionStorageService } from './app-session.service';

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private readonly session = inject(SessionStorageService);
  private readonly calService = inject(CalAngularService);

  getAuthorizationToken(): string | null {
    return this.session.getItem('accessToken');
  }

  isTokenExpired(): boolean {
    const token = this.getAuthorizationToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return true;
      const expiryDate = new Date(exp * 1000);
      return expiryDate < new Date();
    } catch {
      return true;
    }
  }

  /** Fetches a fresh token from CAL and overwrites the stored value. Resolves to whether a token was received. */
  async getUserImpersonationToken(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const envUrl = environment.production ? ENVIRONMENTS.PROD : ENVIRONMENTS.DEV;
      const scope = [`${envUrl}/user_impersonation`];
      let tokenReceived = false;
      this.calService
        .getAADToken(scope)
        .pipe(timeout(environment.requestTimeout))
        .subscribe({
          next: (token: any) => {
            if (token) {
              this.session.setItem('accessToken', token);
            }
            tokenReceived = !!token;
            resolve(tokenReceived);
          },
          error: (error) => {
            console.error('Error while fetching user_impersonation data :', error);
            reject(error);
          },
          complete: () => {
            resolve(tokenReceived);
          },
        });
    });
  }

  private refreshInFlight: Promise<void> | null = null;

  /** Concurrent callers share one CAL renewal so parallel requests cannot start competing popup/redirect fallbacks. */
  refreshToken(): Promise<void> {
    this.refreshInFlight ??= this.getUserImpersonationToken()
      .then(() => undefined)
      .finally(() => {
        this.refreshInFlight = null;
      });
    return this.refreshInFlight;
  }
}
